import React, { useState, useEffect } from 'react';
import { TopBar } from "../../shared/topBar/TopBar";
import { ToastContainer, toast } from "react-toastify";
import { UrlPath } from '../../../config/routeConfig';
import { BreadCrumbs } from '../../shared/breadCrumbs/BreadCrumbs';
import { SBAComboBox } from '../../UiComponents/SBADropDown';
import { AgGridReact } from "ag-grid-react";
import { sbaAxios } from '../../utils/sbaAxios';
import { 
    getPortfolios, 
    assetListUrl, 
    modifyPPLI, 
    getPPLI,
} from '../../../config/apiConfig';
import { Modal } from "react-bootstrap";
import { SBNotify } from '../../utils/sbNotify';
import { SBADrawer } from '../../UiComponents/SBADrawer';
import { useDispatch, useSelector } from 'react-redux';
import { getTaxEntityListAction } from '../../actions/investorAccounts';
import { SBDatePicker } from '../../UiComponents/SBDatePicker';
import moment from 'moment';
import { getPercentageFormat } from '../../utils/conversionMethods';


export const PPLISetup = () => {
    const Level = [{ BreadCrumb: "Admin >", Routes: `${UrlPath.Admin}` },
    { BreadCrumb: "Partnerships >", Routes: `${UrlPath.Partnership}` },
    { BreadCrumb: "PPLI Setup", Routes: `${UrlPath.PPLISetup}`}
    ];
    useEffect(() => {
        document.title = "PPLI Setup";
    }, []);

    const [portfolios, setPortfolios] = useState([]);
    const [selectedPortfolio, setSelectedPortfolio] = useState(null);
    const [assets, setAssets] = useState([]);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [ppliDetails, setPPLIDetails] = useState([]);
    const [isSMA, setIsSMA] = useState(false);
    const [displayHoldbacks, setDisplayHoldbacks] = useState(false);
    const [deleteRow, setDeleteRow] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [showAddDrawer, setShowAddDrawer] = useState(false);
    const [showEditDrawer, setShowEditDrawer] = useState(false);

    const rollBackStates = () => {
        setSelectedPortfolio(null);
        setSelectedAsset(null);
        setIsSMA(false);
        setDisplayHoldbacks(false);
    }

    const fetchPortfolios = async() => {
        try{
            const list = await sbaAxios({
                url:getPortfolios
            })
            setPortfolios(list);
        }
        catch(e){
            setPortfolios([]);
        }
    };

    const fetchAssets = async() => {
        try{
            const list = await sbaAxios({
                url:assetListUrl
            })
            setAssets(list);
        }
        catch(e){
            setAssets([]);
        }
    };

    const fetchPPLI = async() => {
        try{
            const list = await sbaAxios({
                url:getPPLI
            })
            setPPLIDetails(list);
        }
        catch(e){
            setPPLIDetails([])
        }
    };

    useEffect(()=> {
        fetchPortfolios();
        fetchAssets();
        fetchPPLI();
    },[]);

    useEffect(()=>{
        if(!showEditDrawer || showAddDrawer){
            rollBackStates();
        }
    },[showAddDrawer, showEditDrawer]);

    const handleAddPPLI = async() => {
        const pfId = selectedPortfolio?.PortfolioId;
        const asId = selectedAsset?.AssetId;
        const isValidLink = ppliDetails.some((item) => {
            return item.PortfolioId === pfId && item.LinkedAssetId === asId;
        });
        if(isValidLink){
            toast.error('Portfolio Asset Link already exists!');
            return;
        }
        try{
            await sbaAxios({
                url: modifyPPLI+`/${pfId}/${asId}/${isSMA}/${displayHoldbacks}/${false}/${false}`,
                method:'post'
            });
            toast.success('PPLI added successfully');
        }
        catch(e){
            toast.error('Unable to add PPLI');
        }
        finally{
            fetchPPLI();
            rollBackStates();
            setShowAddDrawer(false);
        }
    };

    const handleEditPPLI = async () => {
        const pfId = selectedPortfolio?.PortfolioId;
        const asId = selectedAsset?.AssetId;
        try {
            await sbaAxios({
                url: modifyPPLI + `/${pfId}/${asId}/${isSMA}/${displayHoldbacks}/${true}/${false}`,
                method: 'post',
            });
    
            toast.success('PPLI updated successfully');
        } catch (e) {
            toast.error('Unable to update PPLI');
        }
        finally{
            fetchPPLI();
            rollBackStates();
            setShowEditDrawer(false);
        }
    };

    const handleDeletePPLI = async () => {
        try {
            const { PortfolioId, LinkedAssetId, IsSMA, DisplayHoldbacks } = deleteRow;
    
            await sbaAxios({
                url: modifyPPLI + `/${PortfolioId}/${LinkedAssetId}/${IsSMA}/${DisplayHoldbacks}/${false}/${true}`,
                method: 'post',
            });
    
            toast.success('PPLI deleted successfully');
        } catch (e) {
            toast.error('Unable to delete PPLI');
        }
        finally{
            fetchPPLI();
            setDeleteModal(false);
        }
    };

    const actionCellRenderer = (params) => {
        return (
          <div
            className="actions-cell"
          >
            <i
                className="fas fa-edit"
                style={{ color: "#47A547", margin: "0 8px", cursor: "pointer" }}
                onClick={(e)=>{
                    setSelectedPortfolio(portfolios.find((item) => item.PortfolioId === params.data.PortfolioId));
                    setSelectedAsset(assets.find((item) => item.AssetId === params.data.LinkedAssetId));
                    setIsSMA(params.data.IsSMA);
                    setDisplayHoldbacks(params.data.DisplayHoldbacks);
                    setShowEditDrawer(true);
                }}
            ></i>
            <i
              className="fa fa-trash"
              style={{color: "red",margin: "0 8px",cursor: "pointer",}}
              onClick={(e)=>{
                setDeleteRow(params.data);
                setDeleteModal(true);
              }}
            ></i>
          </div>
        );
    };

    const colDefs = [
        {headerName:'Portfolio', field:"PortfolioName"},
        {headerName:'Asset', field:"LinkedAssetName"},
        {
            headerName: 'Is SMA',
            field: 'IsSMA',
            cellRenderer: 'agCheckboxCellRenderer',
        },
        {
            headerName: 'Display Holdbacks',
            field: 'DisplayHoldbacks',
            cellRenderer: 'agCheckboxCellRenderer',
        },
        {headerName:'Actions', field:'Actions',cellRenderer: actionCellRenderer}
    ]

    return (
        <>
        <TopBar label={"PPLI Setup"} />
        <div className="mainContentData">
            <BreadCrumbs Levels={Level} />
            <div className="container-fluid p-4">
            <div className="row">
                <div className="col d-flex justify-content-end">
                    <button
                        type="submit"
                        className="p-sm-btn"
                        onClick={() => setShowAddDrawer(true)}
                    >
                        Add PPLI
                    </button>
                </div>
            </div>
            <div className="row d-flex mt-3">
                <div className="ag-theme-alpine" style={{ height: "400px", width: "100%", paddingTop: 0 }}>
                    <AgGridReact
                        rowData={ppliDetails}
                        columnDefs={colDefs}
                        defaultColDef={{ resizable: true, sortable: true, filter: true ,flex:1}}
                    />
            </div>
            </div>
            </div>
        </div>
        <ToastContainer/>
        <AddEditDrawer
            open={showAddDrawer}
            setOpen={setShowAddDrawer}
            title={'Add PPLI'}
            portfolios={portfolios}
            selectedPortfolio={selectedPortfolio}
            setSelectedPortfolio={setSelectedPortfolio}
            assets={assets}
            selectedAsset={selectedAsset}
            setSelectedAsset={setSelectedAsset}
            isSMA={isSMA}
            setIsSMA={setIsSMA}
            displayHoldbacks={displayHoldbacks}
            setDisplayHoldbacks={setDisplayHoldbacks}
            handleSave={handleAddPPLI}
        />
        <AddEditDrawer
            open={showEditDrawer}
            setOpen={setShowEditDrawer}
            title={'Edit PPLI'}
            portfolios={portfolios}
            selectedPortfolio={selectedPortfolio}
            setSelectedPortfolio={setSelectedPortfolio}
            assets={assets}
            selectedAsset={selectedAsset}
            setSelectedAsset={setSelectedAsset}
            isSMA={isSMA}
            setIsSMA={setIsSMA}
            displayHoldbacks={displayHoldbacks}
            setDisplayHoldbacks={setDisplayHoldbacks}
            handleSave={handleEditPPLI}
            rollBackStates={rollBackStates}
        />
        <Modal show={deleteModal} onHide={()=>{setDeleteModal(false); setDeleteRow(null)}}>
          <Modal.Header closeButton>
            <Modal.Title>Delete PPLI</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="col">
              Do you really want to delete the selected PPLI?
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="s-sm-btn mx-1" onClick={() => {setDeleteModal(false); setDeleteRow(null)}}>
              Cancel
            </button>
            <button
              className="p-sm-btn mx-1"
              onClick={handleDeletePPLI}
            >
              Delete
            </button>
          </Modal.Footer>
        </Modal>
        </>
    )
}

const AddEditDrawer = ({
    open,
    setOpen,
    title,
    portfolios,
    selectedPortfolio,
    setSelectedPortfolio,
    assets,
    selectedAsset,
    setSelectedAsset,
    isSMA,
    setIsSMA,
    displayHoldbacks,
    setDisplayHoldbacks,
    handleSave,
  }) => {
    
    return (
      <SBADrawer open={open} setOpen={setOpen} title={title} style={{ width: "40vw" }}>
            <div className="row">
                <div className='col-6'>
                <label className="label">Portfolio</label>
                    <div>
                        <SBAComboBox
                            className="w-100"
                            data={portfolios}
                            textField={"Name"}
                            valueField={"PortfolioId"}
                            value={selectedPortfolio}
                            onChange={(e) => setSelectedPortfolio(e.value)}
                            disabled={title === 'Edit PPLI'}
                            popupSettings={{ className: "modalDD" }}
                        ></SBAComboBox>
                    </div>
                </div>
            </div>
            <div className='row'>
            <div className='col-6'>
                <label className="label">Asset</label>
                    <div>
                        <SBAComboBox
                            className="w-100"
                            data={assets}
                            textField={"AssetName"}
                            valueField={"AssetId"}
                            value={selectedAsset}
                            onChange={(e) => setSelectedAsset(e.value)}
                            disabled={title === 'Edit PPLI'}
                            popupSettings={{ className: "modalDD" }}
                        ></SBAComboBox>
                    </div>
                </div>
            </div>
            <div className='row'>
            <div className='col-6' style={{alignContent:'center'}}>
                    <div className='d-flex justify-content-between mt-3'>
                        <div style={{display:'flex', alignItems:'center', gap:'8px', marginRight:'20px'}}>
                            <input
                                type="checkbox"
                                checked={isSMA}
                                onChange={() => setIsSMA(!isSMA)}
                            />
                            <label className="label">Is SMA</label>
                        </div>
                        <div style={{display:'flex', alignItems:'center', gap:'8px', marginRight:'20px'}}>
                            <input
                                type="checkbox"
                                checked={displayHoldbacks}
                                onChange={() => setDisplayHoldbacks(!displayHoldbacks)}
                            />
                            <label className="label">Display Holdbacks</label>
                        </div>
                    </div>
                </div>
            </div>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button className="btn btn-primary" onClick={handleSave} disabled={!selectedPortfolio || !selectedAsset}>
            Save
          </button>
          <button className="btn btn-secondary" onClick={()=>setOpen(false)}>
            Cancel
          </button>
        </div>
      </SBADrawer>
    );
  };